import React, { useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Card, CardContent } from '@frontend/shadcn/components/ui/card';
import { Button, Breadcrumb } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';

const OfferSettingsTab = () => (
  <Card className="bg-white border border-gray-200">
    <CardContent className="p-6">
      <div className="text-center py-12">
        <div className="text-gray-400 mb-4">
          <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Offer Settings</h3>
        <p className="text-gray-600">This section is coming soon.</p>
      </div>
    </CardContent>
  </Card>
);

const EmailTemplatesTab = () => (
  <Card className="bg-white border border-gray-200">
    <CardContent className="p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Email Automations
        </h2>
        <p className="text-sm text-gray-600">
          Automate email tasks to easily scale your offers. Add a new automation or configure an existing one.
        </p>
      </div>

      <div className="mb-6">
        <div className="flex items-center gap-2">
          <div className="w-1 h-4 bg-orange-400 rounded" />
          <span className="text-sm font-medium text-gray-700">Recommended automations</span>
        </div>
      </div>

      <div className="space-y-3">
        {/* Enable Welcome Email */}
        <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
              <span className="text-white text-lg">✉</span>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium text-gray-900">Enable Welcome Email</span>
                <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full font-medium">Active</span>
              </div>
              <p className="text-sm text-gray-600">
                Send a welcome email to creators as soon as they're added to this offer.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-xs text-gray-500">Last Triggered</div>
              <div className="text-sm font-medium text-gray-900">2 hours ago</div>
            </div>
            <div className="text-right">
              <div className="text-xs text-gray-500">Success rate</div>
              <div className="text-sm font-medium text-gray-900">98%</div>
            </div>
            <div className="flex gap-1">
              <Button size="small" type="text" className="text-gray-500 hover:text-gray-700">📊</Button>
              <Button
                size="small"
                type="text"
                className="text-gray-500 hover:text-gray-700"
              >
                ✏️
              </Button>
              <Button size="small" type="text" className="text-gray-500 hover:text-gray-700">•••</Button>
            </div>
          </div>
        </div>

        {/* Other automation cards */}
        {[
          { icon: '🔓', bg: 'bg-purple-500', title: 'Tier unlocked', desc: "Let creators know they've moved up to a new payout tier." },
          { icon: '📈', bg: 'bg-green-500', title: 'X conversions away from next tier', desc: 'Motivate creators who are close to reaching a higher payout tier.' },
          { icon: '💰', bg: 'bg-orange-500', title: 'X sales amount away from next tier', desc: 'Notify when creators are near the next tier based on revenue.' },
          { icon: '🎁', bg: 'bg-pink-500', title: 'Reward unlocked', desc: 'Congratulate creators for unlocking a reward.' },
          { icon: '💎', bg: 'bg-teal-500', title: 'X sales amount away from reward', desc: 'Motivate creators close to unlocking a reward based on sales.' },
          { icon: '🎯', bg: 'bg-blue-600', title: 'X conversions away from reward', desc: "Let creators know they're close to unlocking a reward." }
        ].map((automation, index) => (
          <div key={index} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 ${automation.bg} rounded-full flex items-center justify-center`}>
                <span className="text-white text-lg">{automation.icon}</span>
              </div>
              <div>
                <div className="font-medium text-gray-900 mb-1">{automation.title}</div>
                <p className="text-sm text-gray-600">{automation.desc}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="small"
                type="primary"
                className="bg-blue-600 hover:bg-blue-700 border-blue-600 hover:border-blue-700"
              >
                Activate
              </Button>
            </div>
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

const AutomationsTab = () => (
  <Card className="bg-white border border-gray-200">
    <CardContent className="p-6">
      <div className="text-center py-12">
        <div className="text-gray-400 mb-4">
          <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Automations</h3>
        <p className="text-gray-600">This section is coming soon.</p>
      </div>
    </CardContent>
  </Card>
);

const RunHistoryTab = () => (
  <Card className="bg-white border border-gray-200">
    <CardContent className="p-6">
      <div className="text-center py-12">
        <div className="text-gray-400 mb-4">
          <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Run History</h3>
        <p className="text-gray-600">This section is coming soon.</p>
      </div>
    </CardContent>
  </Card>
);

interface IOfferSettingsPageProps {
  baseUri: string;
}

interface RouteParams {
  offerId: string;
}

export const OfferSettingsPage: React.FC<IOfferSettingsPageProps> = ({
  baseUri,
}) => {
  const { offerId } = useParams<RouteParams>();
  const history = useHistory();
  const [activeTab, setActiveTab] = useState<'offerSettings' | 'emailTemplates' | 'automations' | 'runHistory'>('offerSettings');

  const handleBackClick = () => {
    history.push(`${baseUri}/offers/${offerId}/details`);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'offerSettings':
        return <OfferSettingsTab />;
      case 'emailTemplates':
        return <EmailTemplatesTab />;
      case 'automations':
        return <AutomationsTab />;
      case 'runHistory':
        return <RunHistoryTab />;
      default:
        return <OfferSettingsTab />;
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Breadcrumb */}
        <div className="mb-4">
          <Breadcrumb
            className="text-sm"
          />
        </div>

        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={handleBackClick}
            type="text"
            size="large"
            className="flex items-center justify-center w-10 h-10 border border-gray-300 rounded-lg hover:bg-gray-50"
          />
          <h1 className="text-2xl font-semibold text-gray-900">
            Ambassador Offer
          </h1>
          <Button type="link" className="ml-auto text-blue-600 hover:text-blue-700">
            Need Help?
          </Button>
        </div>

        <div className="flex gap-8">
          {/* Sidebar */}
          <div className="w-64 shrink-0">
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                <h3 className="text-sm font-medium text-gray-900">Offer Settings</h3>
              </div>
              <div className="p-1">
                <div className="space-y-1">
                  <button
                    onClick={() => setActiveTab('offerSettings')}
                    className={`w-full text-left px-2 py-2 rounded-md text-sm font-medium transition-colors ${
                      activeTab === 'offerSettings'
                        ? 'bg-blue-50 text-blue-700 border border-blue-200'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    Offer Settings
                  </button>
                  <div className="mb-2">
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide px-2 py-1">
                      EMAIL TEMPLATE
                    </div>
                  </div>
                  <button
                    onClick={() => setActiveTab('emailTemplates')}
                    className={`w-full text-left px-2 py-2 rounded-md text-sm font-medium transition-colors ${
                      activeTab === 'emailTemplates'
                        ? 'bg-blue-50 text-blue-700 border border-blue-200'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    Automations
                  </button>
                  <button
                    onClick={() => setActiveTab('runHistory')}
                    className={`w-full text-left px-2 py-2 rounded-md text-sm font-medium transition-colors ${
                      activeTab === 'runHistory'
                        ? 'bg-blue-50 text-blue-700 border border-blue-200'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    Run History
                  </button>
                  <button
                    onClick={() => setActiveTab('automations')}
                    className={`w-full text-left px-2 py-2 rounded-md text-sm font-medium transition-colors ${
                      activeTab === 'automations'
                        ? 'bg-blue-50 text-blue-700 border border-blue-200'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    Automations
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {renderTabContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OfferSettingsPage;
